import { useParams } from 'react-router-dom';
import './styles.scss';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select, { SingleValue } from 'react-select';
import Loader from 'src/components/loader';
import { useChannelActions } from 'src/store/channel/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useCategoryActions } from 'src/store/category/actions';
import { RootState } from 'src/store/reducers/state';
import { CollectionVideoModel } from 'src/models/categoryModel';
import { Link } from 'react-router-dom';
import {
  customFilterOption,
  defaultDateFormatter,
  errorMsg,
  getVideoStatus,
  successMsg,
  uploadImageToS3
} from 'src/components/utilities/utils';
import ToastMessage from 'src/components/ToastContainer';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { fetchVideos } from 'src/components/utilities/lazySpeakerLoader';

interface Option {
  label: string;
  value: string;
}

interface VideoOption {
  value: string;
  label: string;
}

const CollectionVideos: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    control
  } = useForm();

  const { id } = useParams<string>();
  const BackgroundInputFile = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Side panel state
  const [isChannelDropdownVisible, setIsChannelDropdownVisible] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState<string>('');
  const [selectedChannelLabel, setSelectedChannelLabel] = useState<string>('');
  const [panelVideos, setPanelVideos] = useState<VideoOption[]>([]);
  const [panelSearch, setPanelSearch] = useState('');
  const [panelPage, setPanelPage] = useState(1);
  const [panelHasMore, setPanelHasMore] = useState(false);
  const [panelLoading, setPanelLoading] = useState(false);
  const [selectedVideoIds, setSelectedVideoIds] = useState<Set<string>>(new Set());

  // Image upload state
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [isUploadToS3Loading, setIsUploadToS3Loading] = useState<boolean>();
  const [isUploadToS3Success, setIsUploadToS3Success] = useState<string>('');

  const dispatch = useDispatch();
  const categoryActions = useCategoryActions(dispatch);
  const channelActions = useChannelActions(dispatch);

  const {
    channelList,
    collectionVideos,
    isCollectionVideosLoading,
    isCollectionVideosSaved,
    isCollectionUpdated,
    isCollectionVideosUpdated,
    error
  } = useSelector((state: RootState) => ({
    channelList: state.channel.channelList,
    collectionVideos: state.category.collectionVideos,
    isCollectionVideosLoading: state.category.isCollectionVideosLoading,
    isCollectionVideosSaved: state.category.isCollectionVideosSaved,
    isCollectionUpdated: state.category.isCollectionUpdated,
    isCollectionVideosUpdated: state.category.isCollectionVideosUpdated,
    error: state.category.error
  }));

  useEffect(() => {
    channelActions.getChannelListRequest();
    if (id) categoryActions.getCollectionVideosRequest(id);
  }, []);

  useEffect(() => {
    if (collectionVideos?.thumbnailUrl) {
      setBackgroundImageUrl(collectionVideos.thumbnailUrl);
    }
  }, [collectionVideos]);

  useEffect(() => {
    if (isCollectionVideosSaved || isUploadToS3Success) {
      if (id) categoryActions.getCollectionVideosRequest(id);
    }
  }, [isCollectionVideosSaved, isUploadToS3Success]);

  useEffect(() => {
    reset(collectionVideos);
  }, [collectionVideos, reset]);

  useEffect(() => {
    if (id) categoryActions.getCollectionVideosRequest(id);
  }, [isCollectionVideosUpdated]);

  useEffect(() => {
    if (isCollectionVideosSaved) {
      successMsg('Collection Content saved successfully.');
    } else if (isCollectionUpdated) {
      successMsg('Collections updated successfully.');
    } else if (error) {
      errorMsg('Error.');
    }
  }, [isCollectionVideosSaved, isCollectionUpdated]);

  useEffect(() => {
    if (isUploadToS3Success === 'fail') {
      errorMsg('File has not been uploaded successfully');
      setIsUploadToS3Success('');
    } else if (isUploadToS3Success === 'success') {
      if (id) {
        successMsg('File uploaded successfully');
        categoryActions.getCollectionRequest(id);
        setIsUploadToS3Success('');
      }
    }
  }, [isUploadToS3Success]);

  const channelOptions: Option[] = channelList.map((channel) => ({
    label: channel.name,
    value: channel.id
  }));

  const loadPanelVideos = useCallback(
    async (channelId: string, search: string, page: number, replace = false) => {
      if (!channelId) return;
      setPanelLoading(true);
      try {
        const result = await fetchVideos(channelId, search, page);
        setPanelVideos((prev) => (replace ? result.options : [...prev, ...result.options]));
        setPanelHasMore(result.hasMore);
        setPanelPage(page);
      } catch (e) {
        errorMsg('Failed to load videos.');
      } finally {
        setPanelLoading(false);
      }
    },
    []
  );

  const handlePanelSearch = (value: string) => {
    setPanelSearch(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      loadPanelVideos(selectedChannelId, value, 1, true);
    }, 350);
  };

  const handleChannelChange = (selectedOption: SingleValue<Option>) => {
    if (!selectedOption || !selectedOption.value) return;
    setSelectedChannelId(selectedOption.value);
    setSelectedChannelLabel(selectedOption.label);
    setPanelVideos([]);
    setPanelSearch('');
    setSelectedVideoIds(new Set());
    setIsChannelDropdownVisible(false);
    setIsSidePanelOpen(true);
    loadPanelVideos(selectedOption.value, '', 1, true);
  };

  const closeSidePanel = () => {
    setIsSidePanelOpen(false);
    setIsChannelDropdownVisible(false);
    setSelectedVideoIds(new Set());
    setPanelSearch('');
    setSelectedChannelId('');
    setSelectedChannelLabel('');
    setPanelVideos([]);
  };

  const toggleVideoSelection = (videoId: string) => {
    setSelectedVideoIds((prev) => {
      const next = new Set(prev);
      next.has(videoId) ? next.delete(videoId) : next.add(videoId);
      return next;
    });
  };

  const handleAddSelectedVideos = async () => {
    if (!id || selectedVideoIds.size === 0) return;
    for (const videoId of Array.from(selectedVideoIds)) {
      const payload: CollectionVideoModel = { collectionId: id, presentationId: videoId };
      categoryActions.addCollectionVideosRequest(payload);
    }
    categoryActions.resetCategoryStateRequest();
    closeSidePanel();
  };

  const handleDoubleClick = (videoId: string) => {
    window.open(`/videos/${videoId}`, '_blank');
  };

  const handleRemoveVideoClick = (videoId: string) => {
    if (id) {
      const data: CollectionVideoModel = { presentationId: videoId, collectionId: id };
      categoryActions.removeCollectionVideosRequest(data);
    }
  };

  const handleCancelClick = () => reset(collectionVideos);

  const onSubmit = (data: any) => {
    if (id) {
      const payloadData = {
        collectionId: id,
        updateData: [
          { op: 'replace', path: 'collection', value: data.collection },
          { op: 'replace', path: 'description', value: data.description },
          { op: 'replace', path: 'accessibility', value: data.accessibility },
          { op: 'replace', path: 'visibility', value: data.visibility }
        ]
      };
      categoryActions.updateCollectionRequest(payloadData);
    }
  };

  const handleBackgroundImageUpload = async (selectedFile: any) => {
    setIsUploadToS3Success('');
    if (selectedFile) {
      uploadImageToS3(
        selectedFile[0],
        `v2/categories/collections/upload/thumbnail/${id}?`,
        setBackgroundImageUrl,
        'setting',
        setIsUploadToS3Loading,
        setIsUploadToS3Success
      );
    }
  };

  const onInputClick = (event: any) => { event.target.value = ''; };

  const handleRemoveBackgroundImage = () => {
    const cId = collectionVideos?.collectionId;
    const payloadData = {
      collectionId: cId,
      updateData: [{ op: 'replace', path: 'thumbnailUrl', value: '' }]
    };
    categoryActions.updateCollectionRequest(payloadData);
    categoryActions.getCollectionRequest(cId);
  };

  const handleSelectBackgroundImageClick = () => {
    if (BackgroundInputFile.current) BackgroundInputFile.current.click();
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const items = Array.from(collectionVideos.videos);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const reorderedVideoIds = items.map((video: any) => video.id);
    if (id) {
      categoryActions.updateCollectionVideoOrderRequest({ collectionId: id, videoIds: reorderedVideoIds });
    }
  };

  return (
    <>
      <ToastMessage />

      {isSidePanelOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={closeSidePanel}
          />

          <div
            className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col"
            onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Add Content</h2>
                {selectedChannelLabel && (
                  <p className="text-xs text-gray-500 mt-0.5">{selectedChannelLabel}</p>
                )}
              </div>
              <button
                type="button"
                onClick={closeSidePanel}
                className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors">
                <i className="fas fa-times text-sm" />
              </button>
            </div>

            {selectedVideoIds.size > 0 && (
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700 font-medium">
                {selectedVideoIds.size} video{selectedVideoIds.size > 1 ? 's' : ''} selected
              </div>
            )}

            {/* Search bar */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={panelSearch}
                  onChange={(e) => handlePanelSearch(e.target.value)}
                  placeholder="Search videos..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
                />
                {panelSearch && (
                  <button
                    type="button"
                    onClick={() => handlePanelSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <i className="fas fa-times-circle text-sm" />
                  </button>
                )}
              </div>
            </div>

            {/* Video list */}
            <div className="flex-1 overflow-y-auto">
              {panelLoading && panelVideos.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <Loader />
                </div>
              ) : panelVideos.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400 gap-2">
                  <i className="fas fa-video text-2xl" />
                  <p className="text-sm">No videos found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {panelVideos.map((video) => {
                    const isSelected = selectedVideoIds.has(video.value);
                    return (
                      <div
                        key={video.value}
                        onClick={() => toggleVideoSelection(video.value)}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                        <div
                          className={`h-5 w-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'}`}>
                          {isSelected && <i className="fas fa-check text-white text-xs" />}
                        </div>
                        <span className={`text-sm flex-1 leading-snug ${isSelected ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                          {video.label}
                        </span>
                      </div>
                    );
                  })}

                  {panelHasMore && (
                    <div className="p-4 flex justify-center">
                      <button
                        type="button"
                        onClick={() => loadPanelVideos(selectedChannelId, panelSearch, panelPage + 1)}
                        disabled={panelLoading}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50">
                        {panelLoading ? 'Loading...' : 'Load more'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={closeSidePanel}
                className="flex-1 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddSelectedVideos}
                disabled={selectedVideoIds.size === 0}
                className="flex-1 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Add{selectedVideoIds.size > 0 ? ` (${selectedVideoIds.size})` : ''}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Main page ── */}
      <div className="collection-main overflow-auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col md:px-4 py-3 rounded">

            {/* Page header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between w-full border-b border-gray-300 pb-3 gap-3 lg:gap-0">
              <div className="flex items-center gap-3">
                <div className="h-7 w-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full">
                  <Link to={`/collections`}>
                    <i className="fas fa-arrow-left mt-2 mb-2"></i>
                  </Link>
                </div>
                <h3 className="text-left text-lg font-semibold text-gray-600">
                  {collectionVideos.collection}
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (collectionVideos?.collectionUrl) {
                      navigator.clipboard.writeText(collectionVideos.collectionUrl);
                      successMsg('URL copied to clipboard!');
                    }
                  }}
                  className="px-6 btn-primary w-28 mr-2 ml-0 mt-0">
                  Share
                </button>
                <button type="submit" className="px-6 btn-primary w-28 mr-2 ml-0 mt-0">
                  Save
                </button>
                <button
                  onClick={handleCancelClick}
                  type="button"
                  className="mx-0 px-6 btn-primary discard-btn w-28 mt-0">
                  Cancel
                </button>
              </div>
            </div>

            {/* Form fields */}
            <div className="flex items-start lg:flex-row flex-col gap-4">
              {!isCollectionVideosLoading || !isUploadToS3Loading ? (
                <>
                  <div className="flex mt-4 gap-3 flex-col lg:w-2/4 w-full">
                    <div className="flex flex-col gap-1 form-group w-full">
                      <label className="block text-sm font-medium">Name</label>
                      <input
                        type="text"
                        className="input-style"
                        {...register('collection', { required: true })}
                      />
                    </div>
                    <div className="flex flex-col gap-1 form-group w-full">
                      <label className="block text-sm font-medium">Description</label>
                      <textarea
                        className="input-style"
                        {...register('description', { required: false })}
                        rows={7}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4 flex-col lg:w-2/4 w-full">
                    <div className="flex gap-7 w-1/2">
                      <div className="flex flex-col gap-1 form-group flex-1">
                        <label className="block text-sm font-medium">Visibility</label>
                        <select className="border input-style" id="visibility" {...register('visibility')}>
                          <option value="">Select</option>
                          <option value="0">Online</option>
                          <option value="1">Offline</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group file-upload flex-1 w-full flex flex-col gap-1">
                      <label>Thumbnail Image</label>
                      <div className="relative channel-setting-image flex justify-center w-full rounded-md overflow-hidden max-w-xl group cursor-pointer">
                        {backgroundImageUrl ? (
                          <img
                            className="w-full object-contain video-image border border-gray-300 rounded-md overflow-hidden"
                            src={backgroundImageUrl}
                          />
                        ) : (
                          <div className="flex flex-wrap justify-center items-center border border-gray-300 w-full btns-main rounded-md overflow-hidden">
                            <button
                              type="button"
                              onClick={handleSelectBackgroundImageClick}
                              className="mx-0 px-6 btn-primary w-28 mt-0">
                              Upload
                            </button>
                          </div>
                        )}
                        <div className={`hidden items-center justify-center gap-3 absolute top-0 bg-black/20 w-full h-full transition-all duration-300 ${backgroundImageUrl !== '' ? 'video-upload-btn' : 'hidden'}`}>
                          <input
                            ref={BackgroundInputFile}
                            type="file"
                            onChange={(e) => handleBackgroundImageUpload(e.target.files)}
                            className="hidden"
                            onClick={onInputClick}
                          />
                          <button
                            onClick={() => { setBackgroundImageUrl(''); handleRemoveBackgroundImage(); }}
                            className="btn-primary cursor-pointer mx-0 mt-0 w-auto">
                            Remove
                          </button>
                          <button
                            type="button"
                            onClick={handleSelectBackgroundImageClick}
                            className="mx-0 px-6 btn-primary w-28 mt-0">
                            Upload
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <Loader />
              )}
            </div>

            {/* Collection List header */}
            <div className="flex items-center flex-wrap justify-between gap-3 mt-4">
              <h3 className="text-left text-lg font-semibold text-gray-600">Collection List</h3>
              <div className="flex items-center gap-3 justify-end">

                {!isChannelDropdownVisible ? (
                  <button
                    type="button"
                    className="btn-primary m-0 w-auto"
                    onClick={() => setIsChannelDropdownVisible(true)}>
                    Add New Content
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    {channelList.length > 0 && (
                      <div className="w-56">
                        <Controller
                          render={({ field: { onChange, value } }) => (
                            <Select
                              autoFocus
                              filterOption={customFilterOption}
                              options={channelOptions}
                              value={channelOptions.find((opt) => opt.value === value) ?? null}
                              placeholder="Select channel..."
                              className="text-sm"
                              onChange={(selectedOption: SingleValue<Option>) => {
                                onChange(selectedOption ? selectedOption.value : null);
                                handleChannelChange(selectedOption);
                              }}
                            />
                          )}
                          name="channelId"
                          control={control}
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsChannelDropdownVisible(false)}
                      className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
                      <i className="fas fa-times text-sm" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Video table */}
            <div className="tab-content-box shadow mt-4 p-4">
              {isCollectionVideosLoading ? (
                <Loader />
              ) : (
                <div id="tabs-list" className="w-full fade show active overflow-x-auto">
                  <div className="flex flex-col gap-3 tab-box">
                    <div className="bg-gray-100 flex">
                      <div className="p-3 text-left font-semibold text-gray-600 custom-blank-wrap">THUMBNAIL</div>
                      <div className="p-3 text-left font-semibold text-gray-600 custom-name">NAME</div>
                      <div className="p-3 text-left font-semibold text-gray-600 custom-col">ACCESS</div>
                      <div className="p-3 text-left font-semibold text-gray-600 custom-col">STATUS</div>
                      <div className="p-3 text-left font-semibold text-gray-600 custom-col">PRICE</div>
                      <div className="p-3 text-left font-semibold text-gray-600 custom-col">DATE</div>
                      <div className="p-3 text-left font-semibold text-gray-600 custom-col">ACTIONS</div>
                    </div>
                    <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId="videos">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="flex flex-col gap-3 table-body">
                            {collectionVideos.videos && collectionVideos.videos.length === 0 ? (
                              <div className="empty-list-msg">No Videos added to the playlist.</div>
                            ) : (
                              collectionVideos.videos?.map((video: any, index: number) => (
                                <Draggable key={video.id} draggableId={video.id} index={index}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className="flex items-center shadow rounded-md cursor-pointer"
                                      onDoubleClick={() => handleDoubleClick(video.id)}>
                                      <div className="p-3 custom-blank-wrap">
                                        <div className="flex items-center">
                                          {video.thumbnailUrl ? (
                                            <img src={video.thumbnailUrl} className="w-44 h-24 object-cover mr-3 rounded" />
                                          ) : (
                                            <img src={`../../assets/img/dummy-image.svg`} className="w-44 h-24 object-contain mr-3 p-3 rounded bg-gray-200 border-0" />
                                          )}
                                        </div>
                                      </div>
                                      <div className="p-3 custom-name">
                                        <span className="font-medium text-gray-900">{video.title}</span>
                                      </div>
                                      <div className="p-3 custom-col">
                                        <span className="font-medium text-gray-900">{video.contentAccessTypeName}</span>
                                      </div>
                                      <div className="p-3 custom-col">
                                        <span className={`font-semibold text-xs p-2.5 rounded ${getVideoStatus(video.status) === 'Stopped' ? 'bg-yellow-100 text-yellow-400'
                                          : getVideoStatus(video.status) === 'Standby' ? 'standbyColor'
                                            : getVideoStatus(video.status) === 'OnDemand' ? 'bg-green-100 text-green-500'
                                              : getVideoStatus(video.status) === 'Offline' || getVideoStatus(video.status) === 'GreenRoom' ? 'bg-gray-100 text-gray-800'
                                                : getVideoStatus(video.status) === 'Live' ? 'bg-red-100 text-red-500'
                                                  : ''
                                          }`}>
                                          {getVideoStatus(video.status)}
                                        </span>
                                      </div>
                                      <div className="p-3 custom-col">
                                        <span className="font-medium text-gray-900">${video.cost}</span>
                                      </div>
                                      <div className="p-3 custom-col">
                                        <span className="font-medium text-gray-900">
                                          {video.displayDate ? defaultDateFormatter(video.displayDate) : ''}
                                        </span>
                                      </div>
                                      <div className="p-3 custom-col">
                                          <button
                                            type="button"
                                          className="btn btn-danger w-auto inline-flex mt-0"
                                          onClick={() => handleRemoveVideoClick(video.id)}>
                                          Remove
                                              </button>
                                        </div>
                                      </div>
                                  )}
                                </Draggable>
                              ))
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default CollectionVideos;